import React, { useState } from 'react';
import { useRouter, useSignal } from '@funtech-int/rival-react';
import {
    SportEvent,
    SportPromoSelection as SportPromoSelectionType,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { usePaymentPlace } from '@funtech-int/yango-shared';

import { useTimeSignal } from '@/shared/hooks/useTimeSignal';
import { startPlaying } from '@/scenarios/legacy-player/components/player-manager/helpers/startPlaying';
import { PlayerContentType } from '@/scenarios/legacy-player/components/feature-player/types/content';
import { usePaymentPage } from '@/shared/hooks/usePaymentPage';
import { PlusPaymentPlace } from '@/shared/constants/payments';

import { EventContent } from '../EventContent/EventContent';
import { PromoEventPreview } from '../PromoEventPreview/PromoEventPreview';
import { PromoCover } from '../PromoCover/PromoCover';
import { useSportPromoLoginUrl } from '../../hooks/useSportPromoLoginUrl';
import { useSportPromoInitialIndex } from '../../hooks/useSportPromoInitialIndex';
import { SportPromoAnalytics } from '../../lib/analytics';
import { sportEventRoute } from '../../routes';
import {
    EventContentAction,
    EventContentActionValue,
} from '../EventContentAction/EventContentAction';

import { AnimatedCarousel } from './_AnimatedCarousel';
import styles from './PromoSelection.module.css';

interface PromoSelectionProps {
    selection: SportPromoSelectionType;
    analytics: SportPromoAnalytics;
}

const TIME_UPDATE_INTERVAL = 60 * 1000;
const PERMITTED_ACTIONS = new Set<EventContentActionValue>(['About', 'Login', 'Watch', 'Offer']);

export const PromoSelection: React.FC<PromoSelectionProps> = ({ selection, analytics }) => {
    const initialIndex = useSportPromoInitialIndex(selection);
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    const router = useRouter();
    const timeSignal = useTimeSignal(TIME_UPDATE_INTERVAL);
    const currentTimestamp = useSignal(timeSignal);

    const currentItem = selection.items[currentIndex];

    const loginUrl = useSportPromoLoginUrl(currentItem);

    const page = usePaymentPage();
    const paymentPlace = usePaymentPlace({
        page,
        places: [PlusPaymentPlace.SportPromoWatchButton],
        extraParams: {
            customProperties: {
                billingFeatureName: currentItem?.viewOption?.billingFeatureNameToBuy,
            },
        },
    });

    if (!currentItem) {
        return null;
    }

    const { coverUrl } = currentItem.getAssets();

    const renderItem = (item: SportEvent, index: number) => {
        const itemEventState = item.getState(currentTimestamp);

        const onShow = () => {
            analytics.cardShowed({
                uuid: item.id,
                title: item.title,
                cardPosition: index,
            });
        };

        const onOfferClick = () => {
            analytics.cardNavigated({
                uuid: item.id,
                title: item.title,
                cardPosition: index,
                to: 'payment',
            });
        };

        const onWatch = () => {
            startPlaying({
                router,
                contentId: item.id,
                contentType: PlayerContentType.SPORT_LIVE,
                isNewPlayerEnabled: false,
                additionalQueryParams: {
                    sportEventId: item.sportEventId,
                },
            });

            analytics.cardNavigated({
                uuid: item.id,
                title: item.title,
                cardPosition: index,
                to: 'player',
            });
        };

        const onAbout = () => {
            router.navigate({ to: sportEventRoute({ id: item.sportEventId }) });
        };

        return {
            active: (
                <EventContent
                    item={item}
                    eventState={itemEventState}
                    onShow={onShow}
                    contentAction={
                        <EventContentAction
                            permittedActions={PERMITTED_ACTIONS}
                            eventState={itemEventState}
                            paymentPlace={paymentPlace}
                            loginUrl={loginUrl}
                            viewOption={currentItem.viewOption}
                            onWatch={onWatch}
                            onOfferClick={onOfferClick}
                            onAbout={onAbout}
                        />
                    }
                />
            ),
            default: (
                <PromoEventPreview
                    item={item}
                    currentTimestamp={currentTimestamp}
                    onShow={onShow}
                />
            ),
            key: String(item.id),
        };
    };

    return (
        <section className={styles.root}>
            <PromoCover coverUrl={coverUrl} alt={currentItem.title} contentKey={currentItem.id} />
            <AnimatedCarousel
                items={selection.items}
                renderItem={renderItem}
                onActiveChange={setCurrentIndex}
            />
        </section>
    );
};
